import { get } from 'lodash-es'

import { formatDayMonth } from 'syft-acp-util/formatting'
import { overbookStatusLabel } from 'syft-acp-core/entities/ShiftList/helpers/constants'
import { OverbookStatus } from 'syft-acp-core/store/shifts/types'

// Derives whether a listing is a "SyftForce" (agency) listing rather than a direct Syft (platform 1) one.
// Replicates the exact lodash path string previously used inline in ListingMainInformationControls so the
// value stays identical for the Stop/Resume offers button and the offers-stopped alert.
export const getIsSyftForce = (listings: any, listingID: number): boolean =>
  Number(get(listings, `entityMap[${listingID}.platform_id`)) !== 1

// Single source of truth for the "offers are stopped" toggle. Drives both the Stop/Resume shift offers
// button label and the "Offers for this shift are stopped" alert (PTE-51963) so the two never diverge.
export const areShiftOffersStopped = (shift: any, isSyftForce: boolean): boolean => {
  const stopOffersForWorkerType = get(shift, 'stop_offers_for_worker_type[0]')
  return Boolean((isSyftForce && stopOffersForWorkerType) || (!isSyftForce && shift?.stop_new_workers))
}

export const setObjectProperty = (object: any, path: string, value: string) => {
  const parts = path.split('.')
  const limit = parts.length - 1
  for (let i = 0; i < limit; ++i) {
    const key = parts[i]
    object = object[key] ?? (object[key] = {})
  }
  const key = parts[limit]
  object[key] = value
}

export const hideAllAreasOrVenues = (attribute: 'area' | 'venue') => (data: any) =>
  !data[`${attribute}s`] ||
  data[`${attribute}s`]?.length < 1 ||
  (data[`${attribute}s`]?.length === 1 && data[`${attribute}s`]?.[0]?.name === data[attribute]?.name)

export const handleConfirmationMessage = ({
  overridePayrate,
  differentRate,
  differentTime,
  overrideTime,
}: {
  overrideTime?: boolean
  overridePayrate?: boolean
  differentRate?: number
  differentTime?: string
}) => {
  if (differentRate && overridePayrate && differentTime && overrideTime) {
    return `This shift contains worker specific Times and Rates, which won’t be affected by higher level edits.`
  }
  if (differentRate && overridePayrate) {
    return `This job contains worker specific rates, which won't be affected by job rate edits.`
  }
  if (differentTime && overrideTime) {
    return `This shift contains worker specific Times, which won't be affected by shift Time edits.`
  }
  return null
}

export const DUPLICATION_STATS = {
  overbook: 'Overbook',
  check_in: 'Check in',
  check_in_overbook: 'CI/OB',
  no_show: 'No show',
  induction: 'Induction',
}

/**
 * Label for a duplicated shift tag. For `overbook` shifts it differentiates by
 * `overbook_status` when present, falling back to the generic "Overbook" label.
 */
export const getDuplicationLabel = (
  category: keyof typeof DUPLICATION_STATS,
  overbookStatus?: OverbookStatus,
) => {
  if (category === 'overbook' && overbookStatus) {
    return overbookStatusLabel[overbookStatus] ?? DUPLICATION_STATS.overbook
  }
  return DUPLICATION_STATS?.[category]
}

export const findJobTagText = ({
  startTime,
  endTime,
  duplicatedShift,
  isUS,
}: {
  startTime: string
  endTime: string
  duplicatedShift?: {
    duplicate_shift_category: keyof typeof DUPLICATION_STATS
    overbook_status?: OverbookStatus
  }
  isUS?: boolean
}) => {
  if (!startTime || !endTime) return null
  const formatedStartTime = formatDayMonth(startTime, isUS)
  const formatedEndTime = formatDayMonth(endTime, isUS)

  const invalidDate = 'Invalid date'
  if (formatedStartTime === invalidDate || formatedEndTime === invalidDate) return null

  const isSameDay = formatedStartTime === formatedEndTime
  const timeText = isSameDay ? formatedStartTime : `${formatedStartTime} to ${formatedEndTime}`
  if (duplicatedShift) {
    const duplicationText = getDuplicationLabel(
      duplicatedShift?.duplicate_shift_category,
      duplicatedShift?.overbook_status,
    )
    if (duplicationText) {
      return `${duplicationText} ${timeText}`
    }
  }

  return timeText
}
